class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0, event);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      return this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        event,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      return fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      return fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(line, quantity, event, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });
    const eventTarget = event.currentTarget instanceof CartRemoveButton ? 'clear' : 'change';

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);

        CartPerformance.measure(`${eventTarget}:paint-updated-sections"`, () => {
          const quantityElement =
            document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
          const items = document.querySelectorAll('.cart-item');

          if (parsedState.errors) {
            quantityElement.value = quantityElement.getAttribute('value');
            this.updateLiveRegions(line, parsedState.errors);
            return;
          }

          this.classList.toggle('is-empty', parsedState.item_count === 0);
          const cartDrawerWrapper = document.querySelector('cart-drawer');
          const cartFooter = document.getElementById('main-cart-footer');

          if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
          if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

          this.getSectionsToRender().forEach((section) => {
            const elementToReplace =
              document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
            elementToReplace.innerHTML = this.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector
            );
          });
          const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
          let message = '';
          if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
            if (typeof updatedValue === 'undefined') {
              message = window.cartStrings.error;
            } else {
              message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
            }
          }
          this.updateLiveRegions(line, message);

          const lineItem =
            document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
          if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
            cartDrawerWrapper
              ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
              : lineItem.querySelector(`[name="${name}"]`).focus();
          } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
          } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
          }
        });

        CartPerformance.measureFromEvent(`${eventTarget}:user-action`, event);

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
              .then(() => CartPerformance.measureFromEvent('note-update:user-action', event));
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}

// Cart PO Validation Script
document.addEventListener('DOMContentLoaded', function() {
  // Select the relevant elements
  const checkoutButton = document.getElementById('checkout');
  const customerPOInput = document.getElementById('customer-po');
  const cartForm = document.getElementById('cart');
  const errorContainer = document.getElementById('cart-errors');
  
  // Function to validate the PO field
  function validatePO() {
    if (!customerPOInput.value.trim()) {
      customerPOInput.classList.add('invalid');
      errorContainer.textContent = 'Please enter a Customer PO or state not required before checkout';
      errorContainer.style.color = 'red';
      errorContainer.style.marginTop = '10px';
      return false;
    } else {
      customerPOInput.classList.remove('invalid');
      errorContainer.textContent = '';
      return true;
    }
  }
  
  // Add input event listener to clear error when typing
  customerPOInput.addEventListener('input', function() {
    if (customerPOInput.value.trim()) {
      customerPOInput.classList.remove('invalid');
      errorContainer.textContent = '';
    }
  });
  
  // Handle form submission
  // if (cartForm) {
  //   cartForm.addEventListener('submit', function(e) {
  //     if (!validatePO()) {
  //       e.preventDefault(); // Prevent form submission
        
  //       // Scroll to the PO field to make error visible
  //       customerPOInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
        
  //       // Focus on the input
  //       customerPOInput.focus();
  //     }
  //   });
  // }
  
  // Add some basic styles for invalid state
  const style = document.createElement('style');
  style.textContent = `
    #customer-po.invalid {
      border-color: red !important;
      outline: 1px solid red;
    }
    #cart-errors {
      font-size: 14px;
      font-weight: 500;
    }
  `;
  document.head.appendChild(style);
});

document.addEventListener('DOMContentLoaded', function() {
  // Select all shipping option radio buttons
  const shippingRadios = document.querySelectorAll('input[name="expedited_shipping[]"]');
  const cartNoteTextarea = document.getElementById('Cart-note');
  const cartForm = document.getElementById('cart');
  
  // Make the textarea read-only
  cartNoteTextarea.setAttribute('readonly', 'readonly');
  
  // Function to update the cart note with selected shipping option
  function updateCartNote(selectedOption) {
    // Format the shipping option text
    const shippingLine = `Shipping Options:\n${selectedOption}`;
    
    // Replace the entire note content with just the shipping option
    cartNoteTextarea.value = shippingLine;
    
    // Trigger a change event on the textarea to ensure Shopify recognizes the change
    const event = new Event('change', { bubbles: true });
    cartNoteTextarea.dispatchEvent(event);
    
    // Save the cart note to ensure it persists
    if (typeof fetch === 'function') {
      fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          note: shippingLine
        })
      })
      .then(response => response.json())
      .then(data => console.log('Cart note updated'))
      .catch(error => console.error('Error updating cart note:', error));
    }
  }
  
  // Add click event listeners to all shipping radio buttons
  shippingRadios.forEach(radio => {
    radio.addEventListener('change', function() {
      if (this.checked) {
        updateCartNote(this.value);
      }
    });
  });
  
  // Initialize with the currently selected option (if any)
  const initialSelectedRadio = document.querySelector('input[name="expedited_shipping[]"]:checked');
  if (initialSelectedRadio) {
    updateCartNote(initialSelectedRadio.value);
  }
  
  // Add event listener to the checkout button to ensure the note is saved
  if (cartForm) {
    cartForm.addEventListener('submit', function(e) {
      // Make sure the current selected option is saved before checkout
      const selectedRadio = document.querySelector('input[name="expedited_shipping[]"]:checked');
      if (selectedRadio) {
        cartNoteTextarea.value = `Shipping Options:\n${selectedRadio.value}`;
      }
    });
  }
});

document.addEventListener('DOMContentLoaded', function() {
  // Get the Customer PO input field
  const customerPoField = document.getElementById('customer-po');
  
  if (customerPoField) {
    // On page load, check if we have a stored value in localStorage
    const storedPo = localStorage.getItem('customerPO');
    
    // If there's a stored value and the field is empty or different, update it
    if (storedPo && customerPoField.value !== storedPo) {
      customerPoField.value = storedPo;
      
      // Also update the cart attributes via AJAX to ensure server-side consistency
      updateCartAttribute(storedPo);
    }
    
    // Add event listener to save the value when it changes
    customerPoField.addEventListener('input', function(e) {
      const newValue = e.target.value;
      
      // Store in localStorage
      localStorage.setItem('customerPO', newValue);
      
      // Update cart attributes via AJAX
      updateCartAttribute(newValue);
    });
    
    // Add event listener for the blur event (when user clicks away from field)
    customerPoField.addEventListener('blur', function(e) {
      const newValue = e.target.value;
      
      // Store in localStorage
      localStorage.setItem('customerPO', newValue);
      
      // Update cart attributes via AJAX
      updateCartAttribute(newValue);
    });
  }
  
  /**
   * Updates the cart attributes via AJAX to store the Customer PO
   * @param {string} poValue - The Customer PO value to store
   */
  function updateCartAttribute(poValue) {
    fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        attributes: {
          'Customer PO': poValue
        }
      })
    })
    .then(response => response.json())
    .then(data => {
    })
    .catch(error => {
      console.error('Error updating cart attributes:', error);
    });
  }
  
  // If checkout button exists, add event listener to ensure PO is saved before checkout
  const checkoutButton = document.getElementById('checkout');
  if (checkoutButton) {
    checkoutButton.addEventListener('click', function(e) {
      // Before redirecting to checkout, ensure PO is stored
      if (customerPoField) {
        localStorage.setItem('customerPO', customerPoField.value);
      }
    });
  }
});

document.addEventListener('DOMContentLoaded', function() {

  
  document.querySelector(".aasaved-cart-actions a:not(.cs-save-trigger)").innerHTML = "View Saved Quotes";

  function updateSaveButtonVisibility() {

    const cartItems = document.querySelector('cart-items');
    const saveButton = document.querySelector('.cs-save-trigger');
    const savedCartActions = document.querySelector('.aasaved-cart-actions');
    

    if (cartItems && saveButton) {

      if (cartItems.classList.contains('is-empty')) {
        saveButton.style.display = 'none';
        savedCartActions.style.paddingBottom = '36px';
        savedCartActions.style.justifyContent = 'center'
      } else {
        savedCartActions.style.justifyContent = 'right'
        saveButton.style.display = '';
        savedCartActions.style.padding = '0';
      }
    }
  }
  updateSaveButtonVisibility();
  
  const cartItems = document.querySelector('cart-items');
  if (cartItems) {
    const observer = new MutationObserver(function(mutations) {
      updateSaveButtonVisibility();
    });
    
    observer.observe(cartItems, { attributes: true, attributeFilter: ['class'] });
  }
});